import { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { hierarchy, pack } from 'd3-hierarchy';
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { isDone } from '../lib/status';
import { fmtInt } from '../lib/format';
import { StatusChip } from './StatusChip';
import {
  aggregateClusters, classifyTicket,
  seriesToFamily, FAMILY_UNKNOWN, SERIES_UNKNOWN,
  BUCKET_LABEL, BUCKET_COLOR,
  type ClusterBucket,
} from '../lib/clusters';
import { createdOf } from '../lib/jira-dates';
import type { Issue } from '../types/issue';

const JIRA_URL_KEY = 'v2.jira.baseUrl';
function loadJiraBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://schueco.atlassian.net';
  return (localStorage.getItem(JIRA_URL_KEY) || 'https://schueco.atlassian.net').replace(/\/+$/, '');
}
const jiraUrl = (base: string, key: string) => `${base}/browse/${encodeURIComponent(key)}`;

type StatusScope = 'open' | 'closed' | 'all';

export function ClusterView() {
  const rowsFull = useFilteredRows() as Issue[];
  const allRows  = useData(s => s.rows) as Issue[];
  const teamFilter = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);
  const jiraBase = useMemo(() => loadJiraBaseUrl(), []);

  const [scope, setScope] = useState<StatusScope>('open');
  const [bucket, setBucket] = useState<ClusterBucket | 'all'>('all');
  const [category, setCategory] = useState<string | null>(null);
  const [family, setFamily]     = useState<string | null>(null);
  const [series, setSeries]     = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [historyMonths, setHistoryMonths] = useState<number>(12);
  const [historyMonth, setHistoryMonth] = useState<string | null>(null); // "YYYY-MM" oder null
  const [labelFilter, setLabelFilter] = useState<string | null>(null);
  const [labelView, setLabelView] = useState<'grid' | 'bubbles' | 'matrix'>('bubbles');
  const exportRef = useRef<HTMLDivElement>(null);

  const allTeams = useMemo(
    () => sortedByCount(countBy(allRows, 'Team Name')),
    [allRows]
  );

  const rows = useMemo(() => {
    if (scope === 'all') return rowsFull;
    return rowsFull.filter(r => {
      const done = isDone(String(r['Status'] ?? ''));
      return scope === 'open' ? !done : done;
    });
  }, [rowsFull, scope]);

  const agg = useMemo(() => aggregateClusters(rows), [rows]);

  // Aktuelle Kategorien und Series in Abhängigkeit vom Bucket
  const categoriesForBucket = useMemo(() => {
    if (bucket === 'all') return [] as { name: string; count: number }[];
    const m = agg.categoryCounts.get(bucket);
    if (!m) return [];
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [agg, bucket]);

  // Familien: entweder scoped auf Kategorie, sonst aggregiert pro Bucket (dedupliziert).
  const familiesForScope = useMemo(() => {
    if (bucket === 'all') return [] as { name: string; count: number }[];
    if (category) {
      const m = agg.familyCounts.get(bucket)?.get(category);
      if (!m) return [];
      return Array.from(m.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    }
    const m = agg.familyByBucket.get(bucket);
    if (!m) return [];
    return Array.from(m.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [agg, bucket, category]);

  // Serien: Kaskade Familie > Kategorie > Bucket-aggregiert.
  const seriesForScope = useMemo(() => {
    if (bucket === 'all') return [] as { name: string; count: number }[];
    if (family && category) {
      const m = agg.seriesByFamily.get(bucket)?.get(category)?.get(family);
      if (!m) return [];
      return Array.from(m.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    }
    if (category) {
      const m = agg.seriesCounts.get(bucket)?.get(category);
      if (!m) return [];
      return Array.from(m.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
    }
    const m = agg.seriesByBucket.get(bucket);
    if (!m) return [];
    return Array.from(m.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [agg, bucket, category, family]);

  // Detail-Tabelle: Tickets die die aktuelle Drill-Down-Auswahl erfüllen
  // Verlauf: Monats-Buckets rückblickend, gestapelt nach aktueller Drill-Down-Ebene.
  // Ebene: bucket → Cluster · category → Kategorien im Bucket · family → Familien in Kategorie/Bucket · series → Serien in Familie.
  const historyChart = useMemo(() => {
    type Level = 'bucket' | 'category' | 'family' | 'series';
    const level: Level = family ? 'series'
      : (bucket !== 'all' && category) ? 'family'
      : (bucket !== 'all') ? 'category'
      : 'bucket';

    const now = new Date();
    // Ende = Anfang nächster Monat (offen, damit alles bis heute drin ist)
    const end   = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const start = new Date(end.getFullYear(),  end.getMonth() - historyMonths, 1);

    const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const months: { key: string; label: string }[] = [];
    for (let i = 0; i < historyMonths; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      months.push({
        key:   monthKey(d),
        label: d.toLocaleString('de-DE', { month: 'short', year: '2-digit' }),
      });
    }

    // Rows: WICHTIG — nur Team- und Status-Filter wirken hier (nicht `rows`, das enthält den Drill-Down).
    // Wir wollen im Verlauf sehen, wie die aktuelle Ebene sich über die Zeit verteilt hat.
    // Wir nutzen `rowsFull` (Team- & globaler Filter) und filtern gemäß aktuellem Drill-Kontext OBERHALB der aktuellen Ebene.
    const perMonthSeg = new Map<string, Map<string, number>>();
    for (const m of months) perMonthSeg.set(m.key, new Map());
    const segTotal = new Map<string, number>();

    for (const r of rowsFull) {
      const created = createdOf(r);
      if (!created) continue;
      if (created < start || created >= end) continue;

      const c = classifyTicket(r);

      // Kontext-Filter oberhalb der aktuellen Ebene
      if (level !== 'bucket') {
        if (!c.buckets.has(bucket as ClusterBucket)) continue;
      }
      if (level === 'family' || level === 'series') {
        if (category) {
          const cats = c.categories.get(bucket as ClusterBucket);
          if (!cats || !cats.has(category)) continue;
        }
      }
      if (level === 'series') {
        if (seriesToFamily(c.series) !== family) continue;
      }

      // Segmente für die aktuelle Ebene bestimmen
      let segments: string[] = [];
      if (level === 'bucket') {
        for (const b of c.buckets) segments.push(BUCKET_LABEL[b]);
      } else if (level === 'category') {
        const cats = c.categories.get(bucket as ClusterBucket);
        segments = cats && cats.size ? Array.from(cats) : ['(unkategorisiert)'];
      } else if (level === 'family') {
        segments = [seriesToFamily(c.series)];
      } else {
        segments = [c.series ?? SERIES_UNKNOWN];
      }

      const bucketMap = perMonthSeg.get(monthKey(created));
      if (!bucketMap) continue;
      for (const seg of segments) {
        bucketMap.set(seg, (bucketMap.get(seg) ?? 0) + 1);
        segTotal.set(seg, (segTotal.get(seg) ?? 0) + 1);
      }
    }

    // Top-N + Sonstige (Cluster-Ebene zeigt fest die 4 Buckets, ohne „Sonstige")
    const sorted = Array.from(segTotal.entries()).sort((a, b) => b[1] - a[1]);
    const TOP_N = level === 'bucket' ? 4 : 8;
    const topKeys = sorted.slice(0, TOP_N).map(([k]) => k);
    const restSet = new Set(sorted.slice(TOP_N).map(([k]) => k));

    const chartData = months.map(m => {
      const seg = perMonthSeg.get(m.key) ?? new Map<string, number>();
      const row: Record<string, unknown> = { month: m.label, _monthKey: m.key };
      let rest = 0;
      for (const [k, v] of seg.entries()) {
        if (restSet.has(k)) rest += v;
        else row[k] = v;
      }
      if (rest > 0) row['Sonstige'] = rest;
      // Alle Top-Keys initialisieren, damit die Stapel-Reihenfolge stimmt
      for (const k of topKeys) if (row[k] == null) row[k] = 0;
      return row;
    });
    const stackKeys = restSet.size > 0 ? [...topKeys, 'Sonstige'] : topKeys;

    const bucketColorByLabel: Record<string, string> = {
      [BUCKET_LABEL.profile]:  BUCKET_COLOR.profile,
      [BUCKET_LABEL.fittings]: BUCKET_COLOR.fittings,
      [BUCKET_LABEL.mco]:      BUCKET_COLOR.mco,
      [BUCKET_LABEL.other]:    BUCKET_COLOR.other,
    };
    const colors: Record<string, string> = {};
    for (let i = 0; i < stackKeys.length; i++) {
      const k = stackKeys[i];
      if (level === 'bucket' && bucketColorByLabel[k]) colors[k] = bucketColorByLabel[k];
      else if (k === 'Sonstige') colors[k] = '#9e9e9e';
      else colors[k] = SERIES_PALETTE[i % SERIES_PALETTE.length];
    }

    const levelLabel: Record<Level, string> = {
      bucket: 'nach Cluster',
      category: `nach Kategorie in ${BUCKET_LABEL[bucket as ClusterBucket]}`,
      family: `nach Familie${category ? ` in „${category}"` : ''}`,
      series: `nach Serie in Familie „${family}"`,
    };

    return { chartData, stackKeys, colors, level, levelLabel: levelLabel[level] };
  }, [rowsFull, bucket, category, family, historyMonths]);

  const detailRows = useMemo(() => {
    const out: Issue[] = [];
    for (const r of rows) {
      const c = classifyTicket(r);
      if (bucket !== 'all' && !c.buckets.has(bucket)) continue;
      if (category) {
        const cats = c.categories.get(bucket === 'all' ? 'other' : bucket);
        // Bei bucket='all' ist Kategorie-Filter irrelevant (kein Sub-Kontext)
        if (bucket !== 'all' && (!cats || !cats.has(category))) continue;
      }
      if (family) {
        const f = seriesToFamily(c.series);
        if (f !== family) continue;
      }
      if (series) {
        const s = c.series ?? '(kein Präfix)';
        if (s !== series) continue;
      }
      if (historyMonth) {
        const created = createdOf(r);
        if (!created) continue;
        const mk = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
        if (mk !== historyMonth) continue;
      }
      if (labelFilter) {
        const labels = (r as Record<string, unknown>)['Labels'];
        const arr = Array.isArray(labels) ? labels : (labels ? [labels] : []);
        if (!arr.some(l => String(l ?? '').trim() === labelFilter)) continue;
      }
      out.push(r);
      if (out.length >= 500) break;
    }
    return out;
  }, [rows, bucket, category, family, series, historyMonth, labelFilter]);

  // Label-Auswertung: respektiert Team + Status + Cluster-Drilldown (ohne 500er-Cap).
  const labelStats = useMemo(() => {
    const counts = new Map<string, number>();
    let ticketsInScope = 0;
    let ticketsWithLabels = 0;
    for (const r of rows) {
      const c = classifyTicket(r);
      if (bucket !== 'all' && !c.buckets.has(bucket)) continue;
      if (category && bucket !== 'all') {
        const cats = c.categories.get(bucket);
        if (!cats || !cats.has(category)) continue;
      }
      if (family && seriesToFamily(c.series) !== family) continue;
      if (series && (c.series ?? SERIES_UNKNOWN) !== series) continue;
      if (historyMonth) {
        const created = createdOf(r);
        if (!created) continue;
        const mk = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
        if (mk !== historyMonth) continue;
      }

      ticketsInScope++;
      const labels = (r as Record<string, unknown>)['Labels'];
      const arr = Array.isArray(labels) ? labels : (labels ? [labels] : []);
      const unique = new Set<string>();
      for (const l of arr) {
        const s = String(l ?? '').trim();
        if (s) unique.add(s);
      }
      if (unique.size > 0) ticketsWithLabels++;
      for (const l of unique) counts.set(l, (counts.get(l) ?? 0) + 1);
    }
    const items = Array.from(counts.entries())
      .map(([name, count]) => ({
        name,
        count,
        pct: ticketsInScope > 0 ? (count / ticketsInScope) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
    return { items, ticketsInScope, ticketsWithLabels, distinct: items.length };
  }, [rows, bucket, category, family, series, historyMonth]);

  // Matrix-Daten: Labels × Teams. WICHTIG: Team-Filter wird für die Matrix
  // ignoriert — sonst gäbe es nur 1 Spalte. Status + Cluster-Drilldown wirken.
  const matrixData = useMemo(() => {
    const topLabels = labelStats.items.slice(0, 30).map(it => it.name);
    const labelSet  = new Set(topLabels);
    // Quelle: allRows ohne Team-Filter, aber mit Scope + Cluster-Drilldown
    const source = allRows.filter(r => {
      if (scope !== 'all') {
        const done = isDone(String(r['Status'] ?? ''));
        if (scope === 'open' ? done : !done) return false;
      }
      const c = classifyTicket(r);
      if (bucket !== 'all' && !c.buckets.has(bucket)) return false;
      if (category && bucket !== 'all') {
        const cats = c.categories.get(bucket);
        if (!cats || !cats.has(category)) return false;
      }
      if (family && seriesToFamily(c.series) !== family) return false;
      if (series && (c.series ?? SERIES_UNKNOWN) !== series) return false;
      if (historyMonth) {
        const created = createdOf(r);
        if (!created) return false;
        const mk = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`;
        if (mk !== historyMonth) return false;
      }
      return true;
    });
    const counts   = new Map<string, Map<string, number>>();
    for (const l of topLabels) counts.set(l, new Map());
    const teamTotals = new Map<string, number>();
    for (const r of source) {
      const team   = String(r['Team Name'] ?? '(kein Team)');
      const labels = (r as Record<string, unknown>)['Labels'];
      const arr    = Array.isArray(labels) ? labels : (labels ? [labels] : []);
      let anyMatch = false;
      for (const l of arr) {
        const name = String(l ?? '').trim();
        if (!labelSet.has(name)) continue;
        anyMatch = true;
        const m = counts.get(name)!;
        m.set(team, (m.get(team) ?? 0) + 1);
      }
      if (anyMatch) teamTotals.set(team, (teamTotals.get(team) ?? 0) + 1);
    }
    const teams = Array.from(teamTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([t]) => t);
    let maxCount = 0;
    for (const m of counts.values()) for (const v of m.values()) if (v > maxCount) maxCount = v;
    return { labels: topLabels, teams, counts, maxCount };
  }, [allRows, scope, bucket, category, family, series, historyMonth, labelStats]);

  function selectBucket(b: ClusterBucket | 'all') {
    setBucket(b);
    setCategory(null);
    setFamily(null);
    setSeries(null);
    setHistoryMonth(null);
    setLabelFilter(null);
  }
  function selectCategory(c: string) {
    setCategory(category === c ? null : c);
    setFamily(null);
    setSeries(null);
    setHistoryMonth(null);
    setLabelFilter(null);
  }
  function selectFamily(f: string) {
    setFamily(family === f ? null : f);
    setSeries(null);
    setHistoryMonth(null);
    setLabelFilter(null);
  }
  function selectSeries(s: string) {
    setSeries(series === s ? null : s);
    setHistoryMonth(null);
    setLabelFilter(null);
  }
  function selectLabel(l: string) {
    setLabelFilter(labelFilter === l ? null : l);
  }

  // Klick auf Balken: Segment + Monat als Filter übernehmen und zur Detail-Tabelle scrollen.
  function selectHistoryBar(monthKey: string, segment: string) {
    if (segment === 'Sonstige') {
      // „Sonstige" ist ein Sammel-Bucket ohne konkrete Auswahl → nur Monat setzen
      setHistoryMonth(historyMonth === monthKey ? null : monthKey);
      return;
    }
    const level = historyChart.level;
    if (level === 'bucket') {
      const bucketByLabel: Record<string, ClusterBucket> = {
        [BUCKET_LABEL.profile]:  'profile',
        [BUCKET_LABEL.fittings]: 'fittings',
        [BUCKET_LABEL.mco]:      'mco',
        [BUCKET_LABEL.other]:    'other',
      };
      const b = bucketByLabel[segment];
      if (b) setBucket(b);
      setCategory(null); setFamily(null); setSeries(null);
    } else if (level === 'category') {
      setCategory(segment === '(unkategorisiert)' ? null : segment);
      setFamily(null); setSeries(null);
    } else if (level === 'family') {
      setFamily(segment);
      setSeries(null);
    } else {
      setSeries(segment);
    }
    setHistoryMonth(monthKey);
  }

  async function exportPdf() {
    const el = exportRef.current;
    if (!el) return;
    setExporting(true);
    try {
      // kleines Delay damit „exporting"-Zustand (falls Buttons ausgeblendet werden) sichtbar wird
      await new Promise(res => setTimeout(res, 30));
      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        // Recharts SVGs korrekt rendern
        onclone: (doc) => {
          // Buttons/Toggles im Export-Klon ausblenden
          doc.querySelectorAll('[data-pdf-hide="true"]').forEach(n => {
            (n as HTMLElement).style.display = 'none';
          });
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgW = pageW - margin * 2;
      const pxPerMm = canvas.width / imgW;
      const pageHeightPx = (pageH - margin * 2) * pxPerMm;

      // Header
      pdf.setFontSize(14);
      pdf.text('SchueCal · Cluster-Auswertung', margin, margin + 4);
      pdf.setFontSize(9);
      pdf.setTextColor(120);
      const meta = [
        `Erstellt: ${new Date().toLocaleString('de-DE')}`,
        `Team: ${teamFilter || 'Alle'}`,
        `Status: ${scope === 'open' ? 'nur offen' : scope === 'closed' ? 'nur geschlossen' : 'alle'}`,
        `Auswahl: ${breadcrumb(bucket, category, family, series)}`,
        `Tickets in Auswahl: ${fmtInt(detailRows.length)}${detailRows.length >= 500 ? '+' : ''}`,
      ].join('  ·  ');
      pdf.text(meta, margin, margin + 9);
      pdf.setTextColor(0);

      // Bild in Seiten schneiden
      const headerOffsetPx = 6 * pxPerMm; // 6mm für Header-Zeile
      let y = 0;
      let firstPage = true;
      while (y < canvas.height) {
        const availPx = firstPage ? pageHeightPx - headerOffsetPx : pageHeightPx;
        const sliceH = Math.min(canvas.height - y, availPx);
        const c2 = document.createElement('canvas');
        c2.width = canvas.width;
        c2.height = sliceH;
        const ctx = c2.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c2.width, c2.height);
        ctx.drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        const img = c2.toDataURL('image/png');
        if (!firstPage) pdf.addPage();
        const drawY = firstPage ? margin + headerOffsetPx / pxPerMm : margin;
        pdf.addImage(img, 'PNG', margin, drawY, imgW, sliceH / pxPerMm);
        y += sliceH;
        firstPage = false;
      }

      const stamp = new Date().toISOString().slice(0, 10);
      const slug = [
        teamFilter || 'alle-teams',
        bucket === 'all' ? 'alle' : bucket,
        category, family, series,
      ].filter(Boolean).join('_').replace(/[^\w-]+/g, '-').toLowerCase();
      pdf.save(`schuecal-cluster_${slug}_${stamp}.pdf`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <Stack spacing={2}>
      {/* Team-Chip-Reihe (wirkt global) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5, color: 'text.secondary' }}>TEAM</Typography>
        <Chip size="small" label="Alle"
          color={teamFilter === '' ? 'primary' : 'default'}
          variant={teamFilter === '' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ team: '' })} clickable />
        {allTeams.map(([name, count]) => (
          <Chip key={name} size="small" label={`${name} (${count})`}
            color={teamFilter === name ? 'primary' : 'default'}
            variant={teamFilter === name ? 'filled' : 'outlined'}
            onClick={() => setFilters({ team: name })} clickable />
        ))}
      </Box>

      {/* Status-Scope + PDF-Export */}
      <Stack direction="row" alignItems="center" spacing={1} data-pdf-hide="true">
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>STATUS</Typography>
        <ToggleButtonGroup size="small" value={scope} exclusive onChange={(_, v) => v && setScope(v as StatusScope)}>
          <ToggleButton value="open">nur offen</ToggleButton>
          <ToggleButton value="closed">nur geschlossen</ToggleButton>
          <ToggleButton value="all">alle</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={exportPdf}
          disabled={exporting}
        >
          {exporting ? 'Erstelle PDF…' : 'PDF exportieren'}
        </Button>
      </Stack>

    <Box ref={exportRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'background.paper' }}>
      {/* Filter-Kontext (im PDF sichtbar) */}
      <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
        <CardContent sx={{ py: 1.25 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>TEAM</Typography>
              <Chip
                size="small"
                label={teamFilter || 'Alle Teams'}
                color="primary"
                variant={teamFilter ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>STATUS</Typography>
              <Chip
                size="small"
                label={scope === 'open' ? 'nur offen' : scope === 'closed' ? 'nur geschlossen' : 'alle'}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>AUSWAHL</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {breadcrumb(bucket, category, family, series)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>TICKETS</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {fmtInt(rows.length)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Torten-Grafik: Bucket-Verteilung visuell */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Cluster-Verteilung
          </Typography>
          <BucketPie
            data={(['profile','fittings','mco','other'] as ClusterBucket[]).map(b => ({
              bucket: b,
              label:  BUCKET_LABEL[b],
              value:  agg.bucketCounts[b],
              color:  BUCKET_COLOR[b],
            }))}
            selected={bucket === 'all' ? null : bucket}
            onSelect={(b) => selectBucket(bucket === b ? 'all' : b)}
          />
        </CardContent>
      </Card>

      {/* Ebene 1: Bucket */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Ebene 1 · Cluster
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Klassifikation automatisch aus Label-Keywords (Beschlag/Klinkung/Riegel/… → Beschläge · Profil/Verglasung/… → Profile).
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`Alle · ${fmtInt(rows.length)}`}
              color={bucket === 'all' ? 'primary' : 'default'}
              variant={bucket === 'all' ? 'filled' : 'outlined'}
              onClick={() => selectBucket('all')}
              clickable
            />
            {(['profile', 'fittings', 'mco', 'other'] as ClusterBucket[]).map(b => {
              const n = agg.bucketCounts[b];
              const active = bucket === b;
              return (
                <Chip
                  key={b}
                  size="small"
                  label={`${BUCKET_LABEL[b]} · ${fmtInt(n)}`}
                  onClick={() => selectBucket(b)}
                  variant={active ? 'filled' : 'outlined'}
                  clickable
                  sx={{
                    bgcolor: active ? BUCKET_COLOR[b] : 'transparent',
                    color:  active ? 'white' : BUCKET_COLOR[b],
                    borderColor: BUCKET_COLOR[b],
                    fontWeight: 700,
                  }}
                />
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Ebene 2: Kategorie */}
      {bucket !== 'all' && categoriesForBucket.length > 0 && (
        <>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Ebene 2 · Kategorie in {BUCKET_LABEL[bucket]} · Verteilung
              </Typography>
              <SeriesPie
                data={categoriesForBucket.map(c => ({ label: c.name, value: c.count }))}
                selected={category}
                onSelect={(name) => selectCategory(name)}
              />
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Ebene 2 · alle Kategorien als Chips
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {categoriesForBucket.map(({ name, count }) => (
                  <Chip
                    key={name}
                    size="small"
                    label={`${name} · ${fmtInt(count)}`}
                    color={category === name ? 'primary' : 'default'}
                    variant={category === name ? 'filled' : 'outlined'}
                    onClick={() => selectCategory(name)}
                    clickable
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Ebene 3: Familie (aggregiert über Bucket, gescoped bei Kategorie-Wahl) */}
      {bucket !== 'all' && familiesForScope.length > 0 && (
        <>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Ebene 3 · Produkt-Familie · Verteilung{category ? ` in Kategorie „${category}"` : ' (alle Kategorien im Bucket)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Familie fasst alle Serien einer Baureihe zusammen (z.B. AWS 75 / AWS 90 → AWS). Klick filtert die Ebene 4 darunter.
              </Typography>
              <SeriesPie
                data={familiesForScope.map(f => ({ label: f.name, value: f.count }))}
                selected={family}
                onSelect={(name) => selectFamily(name)}
              />
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Ebene 3 · alle Familien als Chips
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {familiesForScope.slice(0, 60).map(({ name, count }) => (
                  <Chip
                    key={name}
                    size="small"
                    label={`${name} · ${fmtInt(count)}`}
                    sx={name === FAMILY_UNKNOWN ? { fontStyle: 'italic', opacity: 0.85 } : undefined}
                    color={family === name ? 'primary' : 'default'}
                    variant={family === name ? 'filled' : 'outlined'}
                    onClick={() => selectFamily(name)}
                    clickable
                  />
                ))}
                {familiesForScope.length > 60 && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                    +{familiesForScope.length - 60} weitere
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Ebene 4: Serie — nur wenn Familie gewählt (Aufsplittung der Familie in Serien) */}
      {bucket !== 'all' && family && seriesForScope.length > 0 && (
        <>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Ebene 4 · Serie · Verteilung in Familie „{family}"{category ? ` / Kategorie „${category}"` : ''}
              </Typography>
              <SeriesPie
                data={seriesForScope.map(s => ({ label: s.name, value: s.count }))}
                selected={series}
                onSelect={(name) => selectSeries(name)}
              />
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Ebene 4 · alle Serien als Chips (Familie „{family}")
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {seriesForScope.slice(0, 60).map(({ name, count }) => (
                  <Chip
                    key={name}
                    size="small"
                    label={`${name} · ${fmtInt(count)}`}
                    color={series === name ? 'primary' : 'default'}
                    variant={series === name ? 'filled' : 'outlined'}
                    onClick={() => selectSeries(name)}
                    clickable
                  />
                ))}
                {seriesForScope.length > 60 && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                    +{seriesForScope.length - 60} weitere
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Verlauf: neu erstellte Tickets pro Monat, gestapelt nach aktueller Ebene */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
              Verlauf · neu erstellte Tickets · {historyChart.levelLabel} · letzte {historyMonths} Monate
            </Typography>
            {historyMonth && (
              <Chip
                size="small"
                color="primary"
                label={`Monat: ${formatMonthKey(historyMonth)}`}
                onDelete={() => setHistoryMonth(null)}
                sx={{ fontWeight: 700 }}
              />
            )}
            <ToggleButtonGroup
              size="small"
              value={historyMonths}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                setHistoryMonths(v as number);
                setHistoryMonth(null);
              }}
              data-pdf-hide="true"
            >
              <ToggleButton value={6}>6M</ToggleButton>
              <ToggleButton value={12}>12M</ToggleButton>
              <ToggleButton value={24}>24M</ToggleButton>
              <ToggleButton value={36}>36M</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <HistoryStack
            data={historyChart.chartData}
            keys={historyChart.stackKeys}
            colors={historyChart.colors}
            selectedMonthKey={historyMonth}
            onBarClick={selectHistoryBar}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Klick auf einen Balken übernimmt Segment + Monat in die Auswahl (Detail-Tabelle unten reagiert). Zählt neu erstellte Tickets („Created") pro Monat; Top 8 Segmente + „Sonstige"; Team- und Status-Filter der Ansicht wirken.
          </Typography>
        </CardContent>
      </Card>

      {/* Label-Heatmap */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
            <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
              Labels · Häufigkeit in Auswahl
            </Typography>
            {labelFilter && (
              <Chip
                size="small"
                color="primary"
                label={`Label: ${labelFilter}`}
                onDelete={() => setLabelFilter(null)}
                sx={{ fontWeight: 700 }}
              />
            )}
            <ToggleButtonGroup
              size="small"
              value={labelView}
              exclusive
              onChange={(_, v) => v && setLabelView(v as 'grid' | 'bubbles' | 'matrix')}
              data-pdf-hide="true"
            >
              <ToggleButton value="bubbles">Bubbles</ToggleButton>
              <ToggleButton value="grid">Grid</ToggleButton>
              <ToggleButton value="matrix">Matrix</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {fmtInt(labelStats.distinct)} unterschiedliche Labels bei {fmtInt(labelStats.ticketsWithLabels)} von {fmtInt(labelStats.ticketsInScope)} gefilterten Tickets ({labelStats.ticketsInScope > 0 ? ((labelStats.ticketsWithLabels / labelStats.ticketsInScope) * 100).toFixed(1) : '0'}% haben mindestens 1 Label).
            {labelView === 'bubbles'
              ? ' Bubbles-Ansicht: große Kreise = häufige Labels (zentral), kleine Kreise = selten (außen). Klick wählt das Label als Filter.'
              : labelView === 'matrix'
              ? ' Matrix-Ansicht: Labels (Y) × Teams (X). Zell-Farbe nach Häufigkeit (Jet-Palette blau→rot). Team-Filter wird für die Matrix ignoriert, damit die Teams-Spalten erhalten bleiben. Klick setzt Label (+ Team) als Filter.'
              : ' Grid-Ansicht: Farb-Intensität = Häufigkeit (log-skaliert). Klick wählt das Label als Filter.'}
            {' '}Ein Ticket zählt pro Label nur einmal; ein Ticket mit mehreren Labels erscheint in mehreren Kacheln — Summe der Prozente kann {'>'} 100% werden.
          </Typography>
          {labelView === 'bubbles' ? (
            <LabelBubbles
              items={labelStats.items.slice(0, 120)}
              selected={labelFilter}
              onSelect={selectLabel}
            />
          ) : labelView === 'matrix' ? (
            <LabelMatrix
              data={matrixData}
              selectedLabel={labelFilter}
              selectedTeam={teamFilter}
              onSelectCell={(label, team) => {
                selectLabel(label);
                if (team && teamFilter !== team) setFilters({ team });
              }}
              onSelectLabel={selectLabel}
              onSelectTeam={(team) => setFilters({ team: teamFilter === team ? '' : team })}
            />
          ) : (
            <LabelHeatmap
              items={labelStats.items.slice(0, 60)}
              selected={labelFilter}
              onSelect={selectLabel}
            />
          )}
          {labelView !== 'matrix' && labelStats.items.length > (labelView === 'bubbles' ? 120 : 60) && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Top {labelView === 'bubbles' ? 120 : 60} von {fmtInt(labelStats.items.length)} Labels angezeigt.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>

      {/* Detail-Tabelle (NICHT im PDF-Export enthalten) */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Tickets in Auswahl
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {breadcrumb(bucket, category, family, series)}
            {historyMonth ? ` · Monat ${formatMonthKey(historyMonth)}` : ''}
            {labelFilter ? ` · Label „${labelFilter}"` : ''} · <b>{fmtInt(detailRows.length)}</b> Tickets{detailRows.length >= 500 ? ' (max. 500 angezeigt)' : ''}
          </Typography>
          {detailRows.length === 0 ? (
            <Alert severity="info">Keine Tickets in dieser Auswahl. Team-Filter / Status-Toggle prüfen.</Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Typ</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Serie</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Labels</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRows.map(r => {
                    const key = String(r['Issue key'] ?? '');
                    const cls = classifyTicket(r);
                    const labels = (r['Labels'] ?? []) as unknown;
                    const labelArr = Array.isArray(labels) ? labels.map(String) : (typeof labels === 'string' && labels ? [labels] : []);
                    return (
                      <TableRow key={key} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          <Link href={jiraUrl(jiraBase, key)} target="_blank" rel="noopener noreferrer" underline="hover"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                            {key}
                            <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={String(r['Issue Type'] ?? '')}
                            variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{String(r['Team Name'] ?? '–')}</TableCell>
                        <TableCell><StatusChip status={String(r['Status'] ?? '')} /></TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {cls.series ?? <em style={{ opacity: 0.5 }}>–</em>}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          <Stack direction="row" spacing={0.25} flexWrap="wrap" useFlexGap>
                            {labelArr.slice(0, 5).map(l => (
                              <Chip key={l} size="small" label={l}
                                variant="outlined" sx={{ height: 16, fontSize: 9.5 }} />
                            ))}
                            {labelArr.length > 5 && (
                              <Typography variant="caption" sx={{ opacity: 0.6 }}>+{labelArr.length - 5}</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 460 }}>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(r['Summary'] ?? '')}>
                            {String(r['Summary'] ?? '')}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

function breadcrumb(b: ClusterBucket | 'all', c: string | null, f: string | null, s: string | null): string {
  const parts: string[] = [];
  parts.push(b === 'all' ? 'Alle' : BUCKET_LABEL[b]);
  if (c) parts.push(c);
  if (f) parts.push(f);
  if (s) parts.push(s);
  return parts.join(' › ');
}

// ─── Torten-Grafik ─────────────────────────────────────────────────────

type PieDatum = {
  bucket: ClusterBucket;
  label:  string;
  value:  number;
  color:  string;
};

// Top-N Serien-Torte mit „Sonstige"-Sammler für alles unterhalb der Schwelle.
// Deutliche Farb-Rotation, Klick auf Segment/Zeile wählt Serie im Drill-Down.
const SERIES_PALETTE = [
  '#1976d2', '#ef6c00', '#7b1fa2', '#2e7d32', '#c62828',
  '#00838f', '#5d4037', '#455a64', '#af8700', '#0288d1',
  '#8e24aa', '#43a047',
];

// Jet-Palette (blau → cyan → gelb → rot). Klassische Heatmap-Palette.
// Nicht perzeptuell gleichmäßig — bewusst gewählt, weil im Produkt-Kontext
// die intuitive „kalt = wenig / heiß = viel"-Lesart dominiert.
function jetColor(t: number): string {
  const x = Math.max(0, Math.min(1, t));
  const r = Math.max(0, Math.min(1, 1.5 - Math.abs(4 * x - 3)));
  const g = Math.max(0, Math.min(1, 1.5 - Math.abs(4 * x - 2)));
  const b = Math.max(0, Math.min(1, 1.5 - Math.abs(4 * x - 1)));
  return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
}

// Label-Matrix-Heatmap: 2D-Raster Labels × Teams, Jet-Farbkodierung mit Legende.
// Kleine Zellen bleiben sichtbar durch dünne Trennlinien; leere Zellen bleiben weiß.
function LabelMatrix({ data, selectedLabel, selectedTeam, onSelectCell, onSelectLabel, onSelectTeam }: {
  data: {
    labels: string[];
    teams:  string[];
    counts: Map<string, Map<string, number>>;
    maxCount: number;
  };
  selectedLabel?: string | null;
  selectedTeam?:  string | null;
  onSelectCell?:  (label: string, team: string) => void;
  onSelectLabel?: (label: string) => void;
  onSelectTeam?:  (team: string) => void;
}) {
  if (data.labels.length === 0 || data.teams.length === 0 || data.maxCount === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Keine Daten für die Matrix (kombinierter Filter zu eng).
        </Typography>
      </Box>
    );
  }

  const CELL   = 28;
  const PAD_L  = 180;   // Platz für Label-Namen links
  const PAD_T  = 110;   // Platz für Team-Namen oben (gedreht)
  const PAD_R  = 90;    // Platz für Legende rechts
  const PAD_B  = 20;
  const W      = PAD_L + data.teams.length * CELL + PAD_R;
  const H      = PAD_T + data.labels.length * CELL + PAD_B;

  const LEG_X  = PAD_L + data.teams.length * CELL + 20;
  const LEG_W  = 18;
  const LEG_H  = Math.min(data.labels.length * CELL, 260);
  const LEG_Y  = PAD_T;

  const stops = 32;

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        style={{ maxWidth: '100%', height: 'auto', display: 'block', background: '#fff' }}
      >
        {/* Team-Namen oben (um -45° gedreht) */}
        {data.teams.map((t, j) => {
          const x = PAD_L + j * CELL + CELL / 2;
          const isSel = selectedTeam === t;
          return (
            <g
              key={`th-${t}`}
              transform={`translate(${x}, ${PAD_T - 6}) rotate(-45)`}
              style={{ cursor: onSelectTeam ? 'pointer' : 'default' }}
              onClick={onSelectTeam ? () => onSelectTeam(t) : undefined}
            >
              <text
                textAnchor="start"
                fontSize={11}
                fontWeight={isSel ? 700 : 500}
                fill={isSel ? '#1976d2' : '#334155'}
                style={{ userSelect: 'none' }}
              >
                {t}
              </text>
            </g>
          );
        })}

        {/* Label-Namen links */}
        {data.labels.map((l, i) => {
          const y = PAD_T + i * CELL + CELL / 2;
          const isSel = selectedLabel === l;
          const name = l.length > 26 ? l.slice(0, 25) + '…' : l;
          return (
            <text
              key={`lh-${l}`}
              x={PAD_L - 8}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fontWeight={isSel ? 700 : 500}
              fill={isSel ? '#1976d2' : '#334155'}
              style={{ cursor: onSelectLabel ? 'pointer' : 'default', userSelect: 'none' }}
              onClick={onSelectLabel ? () => onSelectLabel(l) : undefined}
            >
              <title>{l}</title>
              {name}
            </text>
          );
        })}

        {/* Zellen */}
        {data.labels.map((l, i) => (
          data.teams.map((t, j) => {
            const n = data.counts.get(l)?.get(t) ?? 0;
            const x = PAD_L + j * CELL;
            const y = PAD_T + i * CELL;
            const fill = n > 0 ? jetColor(n / data.maxCount) : '#ffffff';
            const isSelCell = selectedLabel === l && selectedTeam === t;
            const showText = n > 0 && CELL >= 24 && n < 1000;
            // Zellen-Text: dunkel über gelb/grün, hell über blau/rot
            const t01 = n / data.maxCount;
            const textLight = t01 < 0.3 || t01 > 0.75;
            return (
              <g
                key={`c-${i}-${j}`}
                style={{ cursor: n > 0 && onSelectCell ? 'pointer' : 'default' }}
                onClick={n > 0 && onSelectCell ? () => onSelectCell(l, t) : undefined}
              >
                <title>{`${l}  ·  ${t}  ·  ${n} Tickets`}</title>
                <rect
                  x={x} y={y} width={CELL} height={CELL}
                  fill={fill}
                  stroke={isSelCell ? '#ff8f00' : '#ffffff'}
                  strokeWidth={isSelCell ? 2 : 1}
                />
                {showText && (
                  <text
                    x={x + CELL / 2}
                    y={y + CELL / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={10}
                    fontWeight={600}
                    fill={textLight ? '#ffffff' : 'rgba(0,0,0,0.78)'}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {n}
                  </text>
                )}
              </g>
            );
          })
        ))}

        {/* Außenrahmen */}
        <rect
          x={PAD_L} y={PAD_T}
          width={data.teams.length * CELL}
          height={data.labels.length * CELL}
          fill="none"
          stroke="#334155"
          strokeWidth={1}
        />

        {/* Farb-Legende (vertikaler Gradient) */}
        <defs>
          <linearGradient id="jet-gradient" x1="0" x2="0" y1="1" y2="0">
            {Array.from({ length: stops }, (_, k) => {
              const t = k / (stops - 1);
              return <stop key={k} offset={`${t * 100}%`} stopColor={jetColor(t)} />;
            })}
          </linearGradient>
        </defs>
        <rect x={LEG_X} y={LEG_Y} width={LEG_W} height={LEG_H} fill="url(#jet-gradient)" stroke="#334155" strokeWidth={0.5} />
        {Array.from({ length: 6 }, (_, k) => {
          const frac  = k / 5;
          const value = Math.round(data.maxCount * (1 - frac));
          const y     = LEG_Y + frac * LEG_H;
          return (
            <g key={`leg-${k}`}>
              <line x1={LEG_X + LEG_W} x2={LEG_X + LEG_W + 4} y1={y} y2={y} stroke="#334155" strokeWidth={0.75} />
              <text x={LEG_X + LEG_W + 6} y={y} dominantBaseline="middle" fontSize={10} fill="#334155" style={{ userSelect: 'none' }}>
                {value}
              </text>
            </g>
          );
        })}
        <text
          x={LEG_X + LEG_W / 2}
          y={LEG_Y + LEG_H + 14}
          textAnchor="middle"
          fontSize={10}
          fill="#334155"
          style={{ userSelect: 'none' }}
        >
          Tickets
        </text>
      </svg>
    </Box>
  );
}

// Label-Bubble-Heatmap: Packed-Circles via d3-hierarchy. Großer Kreis in der
// Mitte = häufigstes Label, kleinere Kreise werden darum herum angeordnet.
// Fläche ∝ Häufigkeit; Farb-Intensität zusätzlich log-skaliert für visuelle
// Tiefe. Beschriftung nur, wenn der Kreis dafür Platz hat.
function LabelBubbles({ items, selected, onSelect }: {
  items: { name: string; count: number; pct: number }[];
  selected?: string | null;
  onSelect?: (label: string) => void;
}) {
  const WIDTH  = 980;
  const HEIGHT = 520;

  const nodes = useMemo(() => {
    if (items.length === 0) return [];
    type Datum = { children?: typeof items; name?: string; count?: number; pct?: number };
    const root = hierarchy<Datum>({ children: items })
      .sum(d => d.count ?? 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    const packed = pack<Datum>().size([WIDTH, HEIGHT]).padding(3)(root);
    return packed.leaves().map(leaf => ({
      x: leaf.x, y: leaf.y, r: leaf.r,
      name:  (leaf.data.name  ?? ''),
      count: (leaf.data.count ?? 0),
      pct:   (leaf.data.pct   ?? 0),
    }));
  }, [items]);

  if (items.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Keine Labels in dieser Auswahl.
        </Typography>
      </Box>
    );
  }

  const maxCount = items[0]?.count ?? 1;
  const logMax = Math.log(maxCount + 1);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {nodes.map(n => {
          const t = Math.log(n.count + 1) / logMax;
          const intensity = 0.25 + t * 0.70; // 0.25..0.95
          const dark = intensity > 0.55;
          const isSel = selected === n.name;
          const isDim = selected != null && !isSel;
          const fill = `rgba(25, 118, 210, ${intensity.toFixed(2)})`;
          // Font-Size proportional zum Radius; nur zeigen wenn Platz für ≥4 Zeichen
          const fontSize = Math.max(10, Math.min(22, n.r * 0.35));
          const maxChars = Math.floor((n.r * 1.6) / (fontSize * 0.55));
          const showText = maxChars >= 4;
          const labelText = showText
            ? (n.name.length > maxChars ? n.name.slice(0, maxChars - 1) + '…' : n.name)
            : '';
          const showCount = n.r >= 22;
          return (
            <g
              key={n.name}
              transform={`translate(${n.x}, ${n.y})`}
              style={{ cursor: onSelect ? 'pointer' : 'default', opacity: isDim ? 0.35 : 1, transition: 'opacity 150ms' }}
              onClick={onSelect ? () => onSelect(n.name) : undefined}
            >
              <title>{`${n.name} · ${n.count} Tickets · ${n.pct.toFixed(1)}%`}</title>
              <circle
                r={n.r}
                fill={fill}
                stroke={isSel ? '#ffb300' : 'rgba(0,0,0,0.12)'}
                strokeWidth={isSel ? 3 : 1}
              />
              {showText && (
                <text
                  textAnchor="middle"
                  dominantBaseline={showCount ? 'alphabetic' : 'middle'}
                  y={showCount ? -2 : fontSize * 0.33}
                  fontSize={fontSize}
                  fontWeight={700}
                  fill={dark ? '#ffffff' : 'rgba(0,0,0,0.87)'}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {labelText}
                </text>
              )}
              {showCount && (
                <text
                  textAnchor="middle"
                  dominantBaseline="hanging"
                  y={2}
                  fontSize={Math.max(9, fontSize * 0.75)}
                  fill={dark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.65)'}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {fmtInt(n.count)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

// Label-Heatmap: Grid aus Kacheln, Farb-Intensität nach Häufigkeit (log-skaliert),
// zeigt Absolut + Prozent. Klick auf eine Kachel setzt sie als Filter (falls Handler).
function LabelHeatmap({ items, selected, onSelect }: {
  items: { name: string; count: number; pct: number }[];
  selected?: string | null;
  onSelect?: (label: string) => void;
}) {
  if (items.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Keine Labels in dieser Auswahl.
        </Typography>
      </Box>
    );
  }
  const max = items[0]?.count ?? 1;
  // Logarithmische Skalierung, damit auch selten vorkommende Labels erkennbar bleiben.
  const logMax = Math.log(max + 1);
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
      gap: 0.75,
    }}>
      {items.map(it => {
        const t = Math.log(it.count + 1) / logMax;              // 0..1
        const intensity = 0.15 + t * 0.75;                      // 0.15..0.90
        const dark = intensity > 0.55;
        const isSel     = selected === it.name;
        const isDim     = selected != null && !isSel;
        const clickable = !!onSelect;
        return (
          <Box
            key={it.name}
            title={`${it.name} · ${it.count} Tickets · ${it.pct.toFixed(1)}%${clickable ? ' — klicken zum Filtern' : ''}`}
            onClick={clickable ? () => onSelect!(it.name) : undefined}
            sx={{
              px: 1, py: 0.75,
              borderRadius: 1,
              bgcolor: `rgba(25, 118, 210, ${intensity.toFixed(2)})`,
              color: dark ? '#fff' : 'rgba(0,0,0,0.87)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.15,
              minHeight: 48,
              cursor: clickable ? 'pointer' : 'default',
              border: isSel ? '2px solid #ffb300' : '1px solid rgba(0,0,0,0.08)',
              boxShadow: isSel ? '0 0 0 2px rgba(255, 179, 0, 0.25)' : 'none',
              opacity: isDim ? 0.45 : 1,
              transition: 'opacity 120ms, box-shadow 120ms, border-color 120ms',
              '&:hover': clickable ? { filter: 'brightness(1.08)' } : {},
            }}
          >
            <Typography sx={{
              fontSize: 11,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.25,
            }}>
              {it.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {fmtInt(it.count)}
              </Typography>
              <Typography sx={{ fontSize: 10.5, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>
                {it.pct.toFixed(1)}%
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

// „YYYY-MM" → „Sep 26" (kurz, für Chip/Header)
function formatMonthKey(key: string): string {
  const [yStr, mStr] = key.split('-');
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10) - 1;
  if (isNaN(y) || isNaN(m)) return key;
  return new Date(y, m, 1).toLocaleString('de-DE', { month: 'short', year: '2-digit' });
}

// Gestapelter Monats-Balken. Erwartet chartData mit `month`, `_monthKey`
// und Zahl-Feldern pro Segment; `keys` legt die Stapel-Reihenfolge fest,
// `colors` mappt Segment→Farbe. Klick auf einen Bar-Abschnitt liefert
// (monthKey, segment) an `onBarClick`.
function HistoryStack({ data, keys, colors, selectedMonthKey, onBarClick }: {
  data: Array<Record<string, unknown>>;
  keys: string[];
  colors: Record<string, string>;
  selectedMonthKey?: string | null;
  onBarClick?: (monthKey: string, segment: string) => void;
}) {
  const total = data.reduce((s, row) => {
    let t = 0;
    for (const k of keys) t += Number(row[k] ?? 0);
    return s + t;
  }, 0);
  if (total === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Keine neu erstellten Tickets im gewählten Zeitraum (Team-/Status-Filter prüfen).
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ width: '100%', height: 300 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" vertical={false} />
          <XAxis dataKey="month" fontSize={11} tickMargin={4} />
          <YAxis fontSize={11} allowDecimals={false} width={38} />
          <ReTooltip
            contentStyle={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.12)', fontSize: 12 }}
            formatter={(v: number, name: string) => [fmtInt(v), name]}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="square" />
          {keys.map(k => (
            <Bar
              key={k}
              dataKey={k}
              stackId="a"
              fill={colors[k] ?? '#9e9e9e'}
              cursor={onBarClick ? 'pointer' : 'default'}
              onClick={(entry: any) => {
                if (!onBarClick) return;
                const payload = entry?.payload ?? entry;
                const monthKey = payload?._monthKey as string | undefined;
                if (monthKey) onBarClick(monthKey, k);
              }}
            >
              {data.map((row, i) => {
                const mk = row._monthKey as string;
                const dim = selectedMonthKey != null && selectedMonthKey !== mk;
                return (
                  <Cell
                    key={`${k}-${mk}-${i}`}
                    fill={colors[k] ?? '#9e9e9e'}
                    fillOpacity={dim ? 0.35 : 1}
                    stroke={selectedMonthKey === mk ? 'rgba(0,0,0,0.55)' : 'none'}
                    strokeWidth={selectedMonthKey === mk ? 1 : 0}
                  />
                );
              })}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function SeriesPie({ data, selected, onSelect }: {
  data: { label: string; value: number }[];
  selected: string | null;
  onSelect: (label: string) => void;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const TOP_N = 10;
  const top   = data.slice(0, TOP_N);
  const rest  = data.slice(TOP_N);
  const restTotal = rest.reduce((s, d) => s + d.value, 0);
  const pieData = [
    ...top.map((d, i) => ({ ...d, color: SERIES_PALETTE[i % SERIES_PALETTE.length], isRest: false })),
    ...(restTotal > 0 ? [{ label: `Sonstige (${rest.length})`, value: restTotal, color: '#9e9e9e', isRest: true }] : []),
  ];
  if (total === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Keine Serien.</Typography>;
  }
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
      <Box sx={{ width: { xs: '100%', md: 260 }, height: 240 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={90}
              paddingAngle={1.5}
              onClick={(_, i) => {
                const d = pieData[i];
                if (!d.isRest) onSelect(d.label);
              }}
              stroke="none"
            >
              {pieData.map((d) => {
                const isDim = selected != null && selected !== d.label;
                return (
                  <Cell
                    key={d.label}
                    fill={d.color}
                    fillOpacity={isDim ? 0.3 : 1}
                    style={{ cursor: d.isRest ? 'default' : 'pointer', outline: 'none' }}
                  />
                );
              })}
            </Pie>
            <ReTooltip
              formatter={(v: number, name: string) => {
                const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
                return [`${fmtInt(v)} (${pct}%)`, name];
              }}
              contentStyle={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.12)' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={0.25}>
          {pieData.map(d => {
            const pct = total > 0 ? (d.value / total) * 100 : 0;
            const active = selected === d.label && !d.isRest;
            return (
              <Box
                key={d.label}
                onClick={() => { if (!d.isRest) onSelect(d.label); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.4,
                  px: 1,
                  borderRadius: 1,
                  cursor: d.isRest ? 'default' : 'pointer',
                  bgcolor: active ? 'action.selected' : 'transparent',
                  '&:hover': d.isRest ? {} : { bgcolor: 'action.hover' },
                  opacity: d.isRest ? 0.75 : 1,
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, fontWeight: active ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.label}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, minWidth: 52, textAlign: 'right' }}>
                  {fmtInt(d.value)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 44, textAlign: 'right' }}>
                  {pct.toFixed(1)}%
                </Typography>
              </Box>
            );
          })}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Top {Math.min(TOP_N, data.length)} von {data.length} Serien. „Sonstige" sammelt alle darunter.
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}

function BucketPie({ data, selected, onSelect }: {
  data: PieDatum[];
  selected: ClusterBucket | null;
  onSelect: (b: ClusterBucket) => void;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const filtered = data.filter(d => d.value > 0);
  if (total === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        Keine Tickets in dieser Auswahl.
      </Typography>
    );
  }
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
      <Box sx={{ width: { xs: '100%', md: 260 }, height: 220 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={filtered}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={85}
              paddingAngle={2}
              onClick={(_, i) => onSelect(filtered[i].bucket)}
              stroke="none"
            >
              {filtered.map((d) => {
                const isDim = selected != null && selected !== d.bucket;
                return (
                  <Cell
                    key={d.bucket}
                    fill={d.color}
                    fillOpacity={isDim ? 0.3 : 1}
                    style={{ cursor: 'pointer', outline: 'none' }}
                  />
                );
              })}
            </Pie>
            <ReTooltip
              formatter={(v: number, name: string) => {
                const pct = total > 0 ? Math.round((v / total) * 100) : 0;
                return [`${fmtInt(v)} (${pct}%)`, name];
              }}
              contentStyle={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.12)' }}
            />
            <Legend
              verticalAlign="bottom"
              iconType="circle"
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value: string) => value}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Stack spacing={0.75}>
          {data.map(d => {
            const pct = total > 0 ? (d.value / total) * 100 : 0;
            const active = selected === d.bucket;
            return (
              <Box
                key={d.bucket}
                onClick={() => onSelect(d.bucket)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: active ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, fontWeight: active ? 700 : 500 }}>
                  {d.label}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, minWidth: 60, textAlign: 'right' }}>
                  {fmtInt(d.value)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'right' }}>
                  {pct.toFixed(1)}%
                </Typography>
              </Box>
            );
          })}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Σ ergibt mehr als 100% wenn Tickets Multi-Component sind (dann in mehreren Buckets gleichzeitig).
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
}
